import { useState } from 'react'
import { useAtomValue } from 'jotai'
import type { FormEventHandler } from 'react'
import { useTranslation } from 'react-i18next'
import { InputRadio } from '@/components/InputRadio/InputRadio'
import { InputSelect } from '@/components/InputSelect/InputSelect'
import { useUpdatePriceIntent } from '@/components/PriceCalculator/useUpdatePriceIntent'
import { priceCalculatorLoadingAtom } from '@/features/priceCalculator/PurchaseForm/priceCalculatorAtoms'
import { usePriceIntent } from '@/features/priceCalculator/usePriceIntent'
import { logger } from '@/services/logger/logger.client'
import type {
  ExtraBuilding,
  ExtraBuildingsField as InputFieldExtraBuildings,
  FieldOption,
} from '@/services/PriceCalculator/Field.types'
import { useShopSessionIdOrThrow } from '@/services/shopSession/ShopSessionContext'
import { Button } from 'ui/src/components/Button/Button'
import { Card } from 'ui/src/components/Card/Card'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { TextField } from 'ui/src/components/TextField/TextField'
import { CrossIcon } from 'ui/src/icons/CrossIcon'
import { CrossIconSmall } from 'ui/src/icons/CrossIconSmall'
import { PlusIcon } from 'ui/src/icons/PlusIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { useTranslateFieldLabel } from './useTranslateFieldLabel'
import { dialogContent, root, sup } from './ExtraBuildingsField.css'

enum RadioOption {
  YES = 'YES',
  NO = 'NO',
}

enum Field {
  Type = 'type',
  Area = 'area',
  HasWaterConnected = 'hasWaterConnected',
}

type ExtraBuildingsFieldProps = {
  field: InputFieldExtraBuildings
  buildingOptions: Array<FieldOption>
}

export function ExtraBuildingsField({ field, buildingOptions }: ExtraBuildingsFieldProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { priceIntent } = usePriceIntent()
  const loading = useAtomValue(priceCalculatorLoadingAtom)
  const shopSessionId = useShopSessionIdOrThrow()

  const { t } = useTranslation('purchase-form')
  const translateLabel = useTranslateFieldLabel()

  const buildingOptionsInput = buildingOptions.map((option) => ({
    name: translateLabel(option.label),
    value: option.value,
  }))

  const updatePriceIntent = useUpdatePriceIntent({
    onSuccess: () => {
      setIsOpen(false)
    },
  })

  if (!priceIntent) {
    logger.error('Price intent is required in ExtraBuildingsField')
    return null
  }

  const priceIntentId = priceIntent.id

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()

    const formData = new FormData(event.currentTarget)
    const data = {
      [field.name]: [...(field.defaultValue ?? []), serializeExtraBuilding(Object.fromEntries(formData.entries()))],
    }
    updatePriceIntent({
      variables: {
        priceIntentId,
        data,
        // This is awkward, but we need to pass variables to second mutation even if its payload is empty here
        customer: { shopSessionId },
      },
    })
  }

  const handleRemove = (extraBuilding: ExtraBuilding) => {
    const identifier = JSON.stringify(extraBuilding)

    const data = {
      [field.name]: (field.defaultValue ?? []).filter((item) => {
        const itemIdentifier = JSON.stringify(item)
        return itemIdentifier !== identifier
      }),
    }
    updatePriceIntent({
      variables: {
        priceIntentId,
        data,
        // This is awkward, but we need to pass variables to second mutation even if its payload is empty here
        customer: { shopSessionId },
      },
    })
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <div className={root}>
        <Text size="xs" color="textSecondary">
          {translateLabel(field.label)}
        </Text>

        {field.defaultValue && field.defaultValue.length > 0 && (
          <ul className={yStack({ gap: 'xs' })}>
            {field.defaultValue.map((item) => {
              const identifier = JSON.stringify(item)
              const buildingOption = buildingOptions.find((option) => option.value === item.type)
              const buildingOptionName = buildingOption?.label ?? { key: item.type }

              return (
                <li key={identifier} className={xStack({ alignItems: 'center', justifyContent: 'space-between' })}>
                  <Text size="md">
                    {/* TODO: Properly localize without string concatenation */}
                    {translateLabel(buildingOptionName)}, {item.area} m<span className={sup}>2</span>
                  </Text>
                  <button
                    style={{ cursor: 'pointer' }}
                    type="button"
                    onClick={() => handleRemove(item)}
                    aria-label={t('FIELD_EXTRA_BUILDINGS_DELETE_BUTTON')}
                  >
                    <CrossIconSmall />
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        <Dialog.Trigger asChild>
          <Button type="button" variant="primary-alt" size="medium">
            <div className={xStack({ gap: 'xs' })}>
              <PlusIcon size="1rem" />
              <span>{t('FIELD_EXTRA_BUILDINGS_ADD_BUTTON')}</span>
            </div>
          </Button>
        </Dialog.Trigger>
      </div>

      <Dialog.Content onClose={() => setIsOpen(false)} frostedOverlay centerContent asChild>
        <Card.Root className={dialogContent}>
          <Card.Aside floating>
            <Dialog.Close type="button">
              <CrossIcon />
            </Dialog.Close>
          </Card.Aside>

          <Card.Header className={xStack({ justifyContent: 'center' })}>
            <Dialog.Title asChild>
              <Heading as="h3" variant="standard.24">
                {t('FIELD_EXTRA_BUILDINGS_ADD_BUTTON')}
              </Heading>
            </Dialog.Title>
          </Card.Header>

          <Card.Content>
            <form onSubmit={handleSubmit}>
              <div className={yStack({ gap: 'lg' })}>
                <div className={yStack({ gap: 'xxs' })}>
                  <InputSelect
                    name={Field.Type}
                    label={t('FIELD_EXTRA_BUILDINGS_TYPE_LABEL')}
                    size="medium"
                    options={buildingOptionsInput}
                    required={true}
                  />
                  <TextField
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="off"
                    name={Field.Area}
                    label={t('FIELD_EXTRA_BUILDINGS_AREA_LABEL')}
                    min={0}
                    suffix="m²"
                    required
                  />
                  <InputRadio
                    name={Field.HasWaterConnected}
                    label={t('FIELD_HAS_WATER_CONNECTED_LABEL')}
                    options={[
                      { label: t('LABEL_NO'), value: RadioOption.NO },
                      { label: t('LABEL_YES'), value: RadioOption.YES },
                    ]}
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  loading={loading}
                  disabled={loading}
                  fullWidth={true}
                >
                  <div className={xStack({ gap: 'xs' })}>
                    <PlusIcon size="1rem" />
                    <span>{t('FIELD_EXTRA_BUILDINGS_MODAL_ADD')}</span>
                  </div>
                </Button>
              </div>
            </form>
          </Card.Content>
        </Card.Root>
      </Dialog.Content>
    </Dialog.Root>
  )
}

function serializeExtraBuilding(data: Record<string, FormDataEntryValue>): ExtraBuilding {
  const type = data[Field.Type]
  if (typeof type !== 'string') {
    throw new Error(`ExtraBuilding.type must be a string: ${data.type}`)
  }

  const areaStr = String(data[Field.Area])
  const area = parseInt(areaStr, 10)
  if (typeof area !== 'number') {
    throw new Error(`ExtraBuilding.area must be a number: ${areaStr}`)
  }

  return {
    type,
    area,
    hasWaterConnected: data[Field.HasWaterConnected] === RadioOption.YES,
  }
}
